import { useState } from "react";
import { useTranslation } from "react-i18next"
import { colors, fonts, glyphs } from "../../../Styles"
import { AttentionCard, Button, Modal, PenModal, SelectField, Widget } from "../..";
import { AttentionTypes, ButtonTypes, FieldStyles, FieldTypes } from "../../../Definitions";

import { Sections } from '../../../Assets/TestData/sectionsData';
import { TestData as Pens } from '../../../Assets/TestData/lf_Data';

/**
 * Attentions
 * @returns A widget with AttentionCards based on pens with alert or warning in the system. Each card opens a Modal with more Pen informations.
 */
export default function Attentions() {
  const {t} = useTranslation();
  const [activePenId, setActivePenId] = useState<number | null>(null); // Pen Id from Attention to show Pen Modal
  const [selectedFilterOption, setSelectedFilterOption] = useState<number | undefined>(0)

  // Attention Card Click
  const handleAttentionClick = (key: number) => {
    setActivePenId(key);
  };
  
  // Banner Content
  const problemsTotal = Pens.filter(pen => pen.attention === AttentionTypes.alert).length;
  const problemText = problemsTotal === 1 ? t("attentions.problem") : t("attentions.problems");

  const warningsTotal = Pens.filter(pen => pen.attention === AttentionTypes.warning).length;
  const warningsText = warningsTotal === 1 ? t("attentions.warning") : t("attentions.warnings");

  // Find Section by PenNo
  const getSectionIdByPenNo = (penId: number): number | null => {
    const section = Sections.find(section => 
      section.pens.some(pen => pen.penId === penId)
    );
  
    return section ? section.id : null;
  };

  // #region | Pen Modal Functionality
  // Pen Modal Btn "Previous / Next Attention"
  const handleAttentionNavigation = (direction: 'previous' | 'next') => {
    if (filteredPens.length === 0 || activePenId === null) return;
    
    const currentIndex = filteredPens.findIndex(pen => pen.penId === activePenId);
    
    let newIndex;
    if (direction === 'previous') {
      newIndex = (currentIndex === 0) ? filteredPens.length - 1 : currentIndex - 1;
    } else {
      newIndex = (currentIndex === filteredPens.length - 1) ? 0 : currentIndex + 1;
    }
  
    setActivePenId(filteredPens[newIndex].penId);
  };
  // #endregion

  // #region | Pen Modal Components (Btns, Seperator)
  // Prev and Next Btns
  const renderNavigationBtns = (direction: 'previous' | 'next') => {
    const content = direction === 'previous'
      ? t("buttons.prev")+" "+t("attentions.attention")
      : t("buttons.next")+" "+t("attentions.attention")
      
    const onClick = () => {
      if (direction === 'previous') {
        handleAttentionNavigation('previous');
      } else if (direction === 'next') {
        handleAttentionNavigation('next')
      }
    };

    const startEnhancers = direction === 'previous'
    ? glyphs.Arrow({rotate: 180})
    : <></>
    const endEnhancer = direction === 'next'
    ? glyphs.Arrow({})
    : <></>
    
    return (
      <Button
        content={content}
        onClick={onClick}
        startEnhancer={startEnhancers}
        endEnhancer={endEnhancer}
        buttonType={ButtonTypes.textButton}
        overrides={{
          baseButton: {...fonts.fontSize4}
        }}
      />
    );
  }
  // #endregion

  // #region | Sorting Functionality
  const filterOptions = [
    {label: t("attentions.attentionType"), id: 0},
    {label: t("locationFeeder.sections.sectionNo"), id: 1}
  ];

  const findLabelById = (id: number | undefined, options: { label: string, id: number }[]) => {
    if (id === undefined) {
      return "";
    }
    const option = options.find(option => option.id === id);
    return option ? option.label : "";
  };

  const handleSelectedFilterOption = (e: any) => {
    const selectedId = e[0]?.id;
    setSelectedFilterOption(selectedId);
  };

  const filteredPens = Pens.filter(
    (pen) => pen.attention === AttentionTypes.alert || pen.attention === AttentionTypes.warning
  ).sort((a, b) => {
    if (selectedFilterOption === 0) {
      if (a.attention === AttentionTypes.alert && b.attention !== AttentionTypes.alert) return -1;
      if (a.attention !== AttentionTypes.alert && b.attention === AttentionTypes.alert) return 1;
    } else if (selectedFilterOption === 1) {
      const sectionA = getSectionIdByPenNo(a.penId);
      const sectionB = getSectionIdByPenNo(b.penId);
      if (sectionA !== sectionB) {
        return (sectionA ?? 0) - (sectionB ?? 0);
      }
    }
    return 0
  });
  //#endregion

  const bannerContent = 
    <div style={{gridArea: "2/1/3/2", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: colors.greyTertiary, height: "30px", ...fonts.fontSize4, ...fonts.header, color: colors.greyPrimary, boxSizing: "border-box"}}>
      {problemsTotal} {problemText} | {warningsTotal} {warningsText}
    </div>

  const headerContent = 
    <div style={{gridArea: "1/1/2/2", width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px", backgroundColor: colors.greyPrimary, boxSizing: "border-box"}}>
      <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyLightTertiary}}>{t("attentions.attentions")}</p>
      <SelectField 
        placeholder={t("filterBy")+"..."}
        overrides={{
          fieldStyle: FieldStyles.greySecondary,
          fieldType: FieldTypes.mediumInputField,
          textStart: true,

        }}
        options={filterOptions} 
        defaultValue={findLabelById(selectedFilterOption, filterOptions)} 
        onChange={handleSelectedFilterOption}
      />
    </div>

    const bodyContent = 
      <div style={{gridArea: "3/1/4/2", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px"}}>
        {filteredPens.map((pen, index) => (
          <AttentionCard
            hover={true}
            onClick={() => handleAttentionClick(pen.penId)}
            key={index}
            attentionType={pen.attention as AttentionTypes}
            systemContent={
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between"}}>
                <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary }}>{t("locationFeeder.sections.section")} {getSectionIdByPenNo(pen.penId)}</p>
                <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary }}>{t("locationFeeder.penPopup.pen")} {pen.penId}</p>
              </div>
            }
            problemContent={
              <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary }}>{pen.error}</p>
            }
          />
        ))}
      </div>
  return(
    <>
      <div style={{width: "210px", height: "100%", backgroundColor: colors.greyLightTertiary, borderRadius: "10px", border: "5px solid"+colors.greyPrimary, boxSizing: "border-box", display: "grid", gridTemplateColumns: "1fr", gridTemplateRows: "75px 30px 1fr"}}>
        {headerContent}
        {bannerContent}
        <div style={{margin: "10px 0px", overflowY: "scroll", height: "calc(100% - 20px)", boxSizing: "border-box", scrollbarColor: colors.greyTertiary+colors.transparent, scrollbarWidth: "thin"}}>
          {bodyContent}
        </div>
      </div>
      {/* ------------- Pen Modal -------------- */}
      <Modal
        isModalOpen={!!activePenId}
        modalHeader={t("locationFeeder.penPopup.pen")+" "+`${activePenId}`}
        modalSubHeader={"System Navn X"+" "+t("locationFeeder.sections.section")+" "+getSectionIdByPenNo(activePenId ?? 0)}
        onCloseBtn={() => {
          setActivePenId(null)
        }}
        modalHeaderContent={
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: "10px", height: "20px" }}>
            <div style={{ height: "20px", display: "flex" }}>
              {renderNavigationBtns('previous')}
            </div> 
            <div style={{ backgroundColor: colors.blueDarkSecondary, height: "20px", width: "1.5px", margin: "0 auto" }}/>
            <div style={{ height: "20px", display: "flex" }}>
              {renderNavigationBtns('next')}
            </div>
          </div>
        }
        overrides={{
          isPrimaryComponent: false,
          root: { padding: "40px 5px 5px 5px" },
          background: colors.greyLightTertiary
        }}
      >
        <PenModal activePenId={activePenId ?? 0}/>
      </Modal>
      {/* ----------- Pen Modal Stop ----------- */}
    </>
  )
}